import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, TrendingUp, TrendingDown, ArrowUpRight, BookOpen, ShoppingCart } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { statusClass } from "@/lib/status";
import { CompanyMonogram } from "@/components/CompanyMonogram";
import { DealCountdown } from "@/components/DealCountdown";

const FILTERS = [
  { label: "All", testid: "opportunities-filter-all" },
  { label: "Finance", testid: "opportunities-filter-finance" },
  { label: "Consumer & FMCG", testid: "opportunities-filter-consumer" },
  { label: "Tech & Health", testid: "opportunities-filter-tech" },
  { label: "Defense & Aerospace", testid: "opportunities-filter-defense" },
  { label: "Infra & Energy", testid: "opportunities-filter-infra" },
  { label: "Sports & Hospitality", testid: "opportunities-filter-sports" },
  { label: "Gold & Precious Metals", testid: "opportunities-filter-gold" },
];

const fmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

export const Opportunities = ({ opportunities, onEnquire }) => {
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");

  const list = useMemo(() => {
    return opportunities.filter((o) => {
      const matchFilter = filter === "All" || o.sector_group === filter;
      const matchQuery = o.name.toLowerCase().includes(query.toLowerCase());
      return matchFilter && matchQuery;
    });
  }, [opportunities, filter, query]);

  return (
    <section id="opportunities" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between"
        >
          <div>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#6D28D9]/90">The Board</p>
            <h2 className="font-display mt-4 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Unlisted Opportunities<span className="text-[#6D28D9]">.</span>
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-500 sm:text-base">
              Indicative unlisted-market pricing across sectors. Illustrative sample data for information only — request a quote for current desk pricing.
            </p>
          </div>
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="opportunities-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search companies…"
              className="w-full border border-slate-200 bg-[#F6F4FD]/70 py-3.5 pl-11 pr-4 text-sm text-slate-800 placeholder-slate-400 outline-none backdrop-blur transition-colors duration-300 focus:border-[#6D28D9]/50"
            />
          </div>
        </motion.div>

        <div className="mt-10 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.label}
              data-testid={f.testid}
              onClick={() => setFilter(f.label)}
              className={`border px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] transition-all duration-300 ${
                filter === f.label
                  ? "border-[#6D28D9] bg-[#6D28D9]/10 text-[#6D28D9]"
                  : "border-slate-200 text-slate-500 hover:border-[#6D28D9]/40 hover:text-slate-900"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {list.map((o, i) => {
            const hasChange = typeof o.change_pct === "number";
            const up = hasChange && o.change_pct >= 0;
            return (
              <motion.article
                key={o.slug}
                data-testid={`opportunity-card-${o.slug}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                className="card-glow group relative flex flex-col border border-slate-200 bg-[#F6F4FD]/70 p-6 backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-[#6D28D9]/40 hover:shadow-[0_12px_40px_-16px_rgba(109,40,217,0.35)] sm:p-7"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3.5">
                    <CompanyMonogram name={o.name} logo={o.logo_path} size="sm" testid={`opportunity-logo-${o.slug}`} />
                    <div className="min-w-0">
                      <span data-testid={`opportunity-sector-${o.slug}`} className="inline-block max-w-full truncate border border-[#6D28D9]/25 bg-[#6D28D9]/[0.07] px-2 py-0.5 font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#6D28D9]">{o.sector}</span>
                      <h3 data-testid={`opportunity-name-${o.slug}`} className="font-display mt-2 text-xl font-bold leading-tight tracking-tight text-slate-900 sm:text-[22px]">{o.name}</h3>
                    </div>
                  </div>
                  <span
                    data-testid={`opportunity-status-${o.slug}`}
                    className={`shrink-0 border px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-[0.18em] ${statusClass(o.status)}`}
                  >
                    {o.status}
                  </span>
                </div>

                <p className="mt-4 text-sm leading-relaxed text-slate-500">{o.highlight}</p>
                {o.closes_at && <div className="mt-3"><DealCountdown closesAt={o.closes_at} testid={`opportunity-deal-${o.slug}`} /></div>}

                <div className="mt-6 flex items-end justify-between border-t border-slate-200 pt-5">
                  <div>
                    <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">Indicative Price</p>
                    <p data-testid={`opportunity-price-${o.slug}`} className="font-display mt-1 text-4xl font-bold tracking-tight text-slate-900">
                      <span className="text-2xl font-semibold text-[#6D28D9]">₹</span>{fmt.format(o.price)}
                    </p>
                  </div>
                  {hasChange ? (
                    <div className={`flex items-center gap-1.5 font-mono2 text-sm ${up ? "text-emerald-600" : "text-rose-600"}`}>
                      {up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                      {up ? "+" : ""}{o.change_pct}% <span className="text-[10px] text-slate-500">1Y</span>
                    </div>
                  ) : (
                    <span data-testid={`opportunity-change-na-${o.slug}`} className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-400">1Y change · n/a</span>
                  )}
                </div>

                <dl className="mt-4 grid grid-cols-2 gap-px border border-slate-200 bg-slate-200 text-left">
                  <div className="bg-white px-3 py-2"><dt className="font-mono2 text-[8px] uppercase tracking-[0.2em] text-slate-400">Min lot</dt><dd className="font-mono2 text-xs text-slate-800">{new Intl.NumberFormat("en-IN").format(o.lot_size)} shares</dd></div>
                  <div className="bg-white px-3 py-2"><dt className="font-mono2 text-[8px] uppercase tracking-[0.2em] text-slate-400">Market cap</dt><dd data-testid={`opportunity-mcap-${o.slug}`} className={`font-mono2 text-xs ${o.market_cap ? "text-slate-800" : "text-slate-400"}`}>{o.market_cap || "Not disclosed"}</dd></div>
                </dl>

                <div className="mt-4 flex items-center justify-between gap-3">
                  <Link
                    data-testid={`opportunity-research-link-${o.slug}`}
                    to={`/company/${o.slug}`}
                    className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500 transition-colors duration-300 hover:text-[#6D28D9]"
                  >
                    <BookOpen className="h-3.5 w-3.5" /> Research
                  </Link>
                  <Link
                    data-testid={`opportunity-buy-link-${o.slug}`}
                    to={`/company/${o.slug}#buy`}
                    className="flex items-center gap-2 bg-[#6D28D9] px-4 py-2 font-mono2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FFFFFF] transition-colors duration-300 hover:bg-[#5B21B6]"
                  >
                    <ShoppingCart className="h-3.5 w-3.5" /> Buy
                  </Link>
                </div>
                <button
                  data-testid={`opportunity-enquire-button-${o.slug}`}
                  onClick={() => { onEnquire(o.name); scrollToId("#enquire"); }}
                  className="mt-3 flex items-center gap-2 self-start font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-all duration-300 hover:gap-3 hover:text-[#6D28D9]"
                >
                  Request Quote <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
              </motion.article>
            );
          })}
        </div>

        {list.length === 0 && (
          <p data-testid="opportunities-empty" className="mt-16 text-center font-mono2 text-xs uppercase tracking-[0.25em] text-slate-500">
            No opportunities match your search
          </p>
        )}
      </div>
    </section>
  );
};
